import { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Tag, Upload, Pin, PinOff, GripVertical, FileText, Download, Archive, ArchiveRestore, Search } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableItem({ brand, handleTogglePin, handleEdit, handleDelete, handleToggleArchive }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: brand.id, disabled: !brand.is_pinned });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className={`shadow-lg hover:shadow-xl transition-all border-0 overflow-hidden ${brand.is_archived ? 'opacity-60' : ''}`} data-testid={`brand-card-${brand.name}`}>
        {brand.image_url && (
          <div className="h-48 sm:h-56 md:h-64 overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
            <img 
              src={brand.image_url} 
              alt={brand.name}
              className="w-full h-full object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}
        <CardHeader className={`${brand.is_archived ? 'bg-gradient-to-br from-gray-500 to-gray-600' : 'bg-gradient-to-br from-purple-500 to-pink-500'} text-white pb-3`}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm sm:text-base md:text-lg flex items-center flex-1 min-w-0">
              {brand.is_pinned && (
                <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mr-1 sm:mr-2 flex-shrink-0">
                  <GripVertical className="w-3 h-3 sm:w-4 sm:h-4" />
                </div>
              )}
              {brand.is_pinned && <Pin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />}
              {brand.is_archived && <Archive className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />}
              <Tag className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="truncate">{brand.name}</span>
            </CardTitle>
            <div className="flex space-x-0.5 sm:space-x-1 flex-shrink-0">
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20"
                onClick={() => handleToggleArchive(brand.id)}
                data-testid={`archive-brand-${brand.name}`}
                title={brand.is_archived ? 'Arşivden Çıkar' : 'Arşivle'}
              >
                {brand.is_archived ? <ArchiveRestore className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> : <Archive className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
              </Button>
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20"
                onClick={() => handleTogglePin(brand.id)}
                data-testid={`pin-brand-${brand.name}`}
                title={brand.is_pinned ? 'Sabitlemeyi kaldır' : 'Sabitle'}
              >
                {brand.is_pinned ? <PinOff className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> : <Pin className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
              </Button>
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20"
                onClick={() => handleEdit(brand)}
                data-testid={`edit-brand-${brand.name}`}
              >
                <Edit2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              </Button>
              <Button 
                size="icon" 
                variant="ghost" 
                className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-red-500/20"
                onClick={() => handleDelete(brand.id)}
                data-testid={`delete-brand-${brand.name}`}
              >
                <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-3">
          <a 
            href={brand.link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs sm:text-sm text-purple-600 hover:text-purple-700 break-all"
          >
            {brand.link}
          </a>
          {brand.is_archived && (
            <p className="text-xs text-gray-500 mt-1">Arşivlenmiş - Menülerde görünmez</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function Brands({ selectedTeam }) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    link: '',
    website_url: '',
    image_url: '',
    channel_id: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // CSV Bulk Upload states
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  
  // Archive filter state
  const [showArchived, setShowArchived] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filtered brands based on search
  const filteredBrands = useMemo(() => {
    if (!searchQuery.trim()) return brands;
    const query = searchQuery.toLowerCase().trim();
    return brands.filter(brand => 
      brand.name.toLowerCase().includes(query) ||
      (brand.link && brand.link.toLowerCase().includes(query))
    );
  }, [brands, searchQuery]);

  useEffect(() => {
    if (selectedTeam) {
      loadBrands();
    }
  }, [selectedTeam, showArchived]);

  const loadBrands = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/brands?include_archived=${showArchived}`);
      setBrands(res.data);
    } catch (err) {
      toast.error('Markalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.target.files?.[0];
    if (!file) {
      // No file selected, keep focus on current window
      window.focus();
      return;
    }

    // Immediately re-focus current window after file selection
    window.focus();
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await axiosInstance.post('/upload-image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Backend now returns full URL
      const imageUrl = res.data.url;
      setFormData({ ...formData, image_url: imageUrl });
      setImagePreview(imageUrl);
      toast.success('Resim yüklendi');
      
      // Ensure focus stays on current window
      window.focus();
    } catch (err) {
      toast.error('Resim yüklenemedi');
      window.focus();
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
      // Final focus to ensure we stay on current window
      window.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBrand) {
        await axiosInstance.put(`/teams/${selectedTeam}/brands/${editingBrand.id}`, formData);
        toast.success('Marka güncellendi');
      } else {
        await axiosInstance.post(`/teams/${selectedTeam}/brands`, formData);
        toast.success('Marka eklendi');
      }
      setDialogOpen(false);
      setFormData({ name: '', link: '', website_url: '', image_url: '' });
      setImagePreview(null);
      setEditingBrand(null);
      loadBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const fixImageUrl = (url) => {
    if (!url) return null;
    // If already absolute URL, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Convert relative URL to absolute
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name,
      link: brand.link,
      website_url: brand.website_url || '',
      image_url: brand.image_url || '',
      channel_id: brand.channel_id || ''
    });
    setImagePreview(fixImageUrl(brand.image_url));
    setDialogOpen(true);
  };

  const handleDelete = async (brandId) => {
    if (!window.confirm('Bu markayı silmek istediğinizden emin misiniz?')) return;
    
    try {
      await axiosInstance.delete(`/teams/${selectedTeam}/brands/${brandId}`);
      toast.success('Marka silindi');
      loadBrands();
    } catch (err) {
      toast.error('Marka silinemedi');
    }
  };

  const openNewDialog = () => {
    setEditingBrand(null);
    setFormData({ name: '', link: '', website_url: '', image_url: '', channel_id: '' });
    setImagePreview(null);
    setDialogOpen(true);
  };

  const handleTogglePin = async (brandId) => {
    try {
      const res = await axiosInstance.post(`/teams/${selectedTeam}/brands/${brandId}/toggle-pin`);
      toast.success(res.data.is_pinned ? 'Marka sabitlendi' : 'Sabitleme kaldırıldı');
      loadBrands();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const handleToggleArchive = async (brandId) => {
    try {
      const res = await axiosInstance.post(`/teams/${selectedTeam}/brands/${brandId}/toggle-archive`);
      toast.success(res.data.message);
      loadBrands();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  // CSV Bulk Upload functions
  const handleCsvFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Sadece CSV dosyaları desteklenmektedir');
      return;
    }

    setCsvFile(file);
    setPreviewLoading(true);
    setPreviewData([]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axiosInstance.post(`/teams/${selectedTeam}/brands/bulk-upload-preview`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setPreviewData(res.data.preview || []);
      toast.success(`${res.data.total} kayıt önizlendi`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Önizleme başarısız');
      setCsvFile(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleBulkImport = async () => {
    if (!csvFile) return;

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const res = await axiosInstance.post(`/teams/${selectedTeam}/brands/bulk-upload-confirm`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`${res.data.created} yeni marka eklendi, ${res.data.updated} marka güncellendi`);
      setBulkDialogOpen(false);
      setCsvFile(null);
      setPreviewData([]);
      loadBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İçe aktarma başarısız');
    } finally {
      setImportLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/brands/csv-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'brands_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Şablon indirildi');
    } catch (err) {
      toast.error('Şablon indirilemedi');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = brands.findIndex((b) => b.id === active.id);
      const newIndex = brands.findIndex((b) => b.id === over.id);

      const newBrands = arrayMove(brands, oldIndex, newIndex);
      setBrands(newBrands);

      // Send all items with their order
      const itemsToUpdate = newBrands.map((item, index) => ({
        id: item.id,
        order: index,
        ...(item.is_pinned && { pin_order: newBrands.filter(b => b.is_pinned).indexOf(item) })
      }));

      try {
        await axiosInstance.post(`/teams/${selectedTeam}/brands/reorder`, {
          items: itemsToUpdate
        });
        toast.success('Sıralama kaydedildi');
      } catch (err) {
        toast.error('Sıralama kaydedilemedi');
        loadBrands();
      }
    }
  };

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <Card className="w-full max-w-md text-center overflow-hidden">
          <CardContent className="pt-8 pb-8 sm:pt-12 sm:pb-12">
            <Tag className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-purple-500" />
            <h2 className="text-xl sm:text-2xl font-bold mb-2">Mağaza Seçin</h2>
            <p className="text-sm sm:text-base text-gray-600">Markaları yönetmek için bir mağaza seçin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="brands-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Markalar</h1>
          <p className="text-sm sm:text-base text-gray-600">Markalarınızı yönetin</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Marka ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-48"
              data-testid="brand-search-input"
            />
          </div>
          <Button
            variant={showArchived ? "default" : "outline"}
            onClick={() => setShowArchived(!showArchived)}
            className={showArchived ? "bg-gray-600 hover:bg-gray-700" : "border-gray-400 text-gray-600 hover:bg-gray-50"}
            data-testid="toggle-archived-btn"
          >
            <Archive className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            {showArchived ? 'Arşivi Gizle' : 'Arşivi Göster'}
          </Button>
          <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="border-purple-500 text-purple-600 hover:bg-purple-50 w-full sm:w-auto"
              >
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Toplu Yükle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-lg sm:text-xl">Toplu Marka Yükleme</DialogTitle>
                <DialogDescription className="text-sm">
                  CSV dosyası ile birden fazla marka ekleyin veya güncelleyin
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-2xl border border-blue-200">
                  <div className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Örnek CSV Şablonu</p>
                      <p className="text-xs text-blue-700">Dosya formatını görmek için şablonu indirin</p>
                    </div>
                  </div>
                  <Button 
                    onClick={downloadTemplate}
                    variant="outline"
                    size="sm"
                    className="border-blue-600 text-blue-600 hover:bg-blue-100"
                  >
                    İndir
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="csv_upload" className="text-sm sm:text-base">CSV Dosyası Seçin</Label>
                  <label
                    htmlFor="csv_upload"
                    className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer hover:border-purple-500 transition-colors bg-gray-50"
                  >
                    <div className="text-center">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        {csvFile ? csvFile.name : 'CSV dosyası seçin'}
                      </p>
                      {previewLoading && <p className="text-xs text-purple-600 mt-1">Yükleniyor...</p>}
                    </div>
                  </label>
                  <input
                    id="csv_upload"
                    type="file"
                    accept=".csv"
                    onChange={handleCsvFileSelect}
                    className="hidden"
                    disabled={previewLoading}
                  />
                </div>

                {previewData.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm sm:text-base">Önizleme ({previewData.length} kayıt)</Label>
                    <div className="border rounded-2xl overflow-hidden">
                      <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left">Durum</th>
                              <th className="px-4 py-2 text-left">Marka Adı</th>
                              <th className="px-4 py-2 text-left">Link</th>
                              <th className="px-4 py-2 text-left">Resim</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.map((item, index) => (
                              <tr key={index} className="border-t hover:bg-gray-50">
                                <td className="px-4 py-2">
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    item.status === 'new' 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {item.status === 'new' ? 'Yeni' : 'Güncelleme'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 font-medium">{item.name}</td>
                                <td className="px-4 py-2 text-xs truncate max-w-xs">{item.link}</td>
                                <td className="px-4 py-2 text-xs truncate max-w-xs">
                                  {item.image_url ? '✓' : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setBulkDialogOpen(false);
                    setCsvFile(null);
                    setPreviewData([]);
                  }}
                  className="w-full sm:w-auto"
                >
                  İptal
                </Button>
                <Button 
                  onClick={handleBulkImport}
                  disabled={!csvFile || previewData.length === 0 || importLoading}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 w-full sm:w-auto"
                >
                  {importLoading ? 'İçe Aktarılıyor...' : `${previewData.length} Kayıt İçe Aktar`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={openNewDialog}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg w-full sm:w-auto"
                data-testid="create-brand-button"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Yeni Marka
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] sm:max-w-md" data-testid="brand-dialog">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl">{editingBrand ? 'Markayı Düzenle' : 'Yeni Marka Ekle'}</DialogTitle>
              <DialogDescription className="text-sm">Marka bilgilerini girin</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm sm:text-base">Marka Adı *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="BILJANA"
                    required
                    data-testid="brand-name-input"
                    className="text-sm sm:text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="link" className="text-sm sm:text-base">Link *</Label>
                  <Input
                    id="link"
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    placeholder="https://t.me/delfiee_biljana"
                    required
                    data-testid="brand-link-input"
                    className="text-sm sm:text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website_url" className="text-sm sm:text-base">Website Linki (opsiyonel)</Label>
                  <Input
                    id="website_url"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                    placeholder="https://delfieestore.com/collections/all-black"
                    data-testid="brand-website-url-input"
                    className="text-sm sm:text-base"
                  />
                  <p className="text-xs text-gray-500">Girilirse QR Poster &quot;Marka Website&quot; QR'ında bu link kullanılır (şablonu geçersiz kılar).</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="channel_id" className="text-sm sm:text-base">Telegram Kanal ID</Label>
                  <Input
                    id="channel_id"
                    value={formData.channel_id}
                    onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })}
                    placeholder="-1001234567890"
                    className="text-sm sm:text-base"
                  />
                  <p className="text-xs text-gray-500">Markanın kendi telegram kanalı ID'si (opsiyonel)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="image_upload" className="text-sm sm:text-base">Marka Görseli</Label>
                  {imagePreview && (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full max-h-48 sm:max-h-64 object-contain rounded-2xl border-2 border-gray-200 bg-gray-50"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="destructive"
                        className="absolute top-2 right-2 h-8 w-8"
                        onClick={() => {
                          setImagePreview(null);
                          setFormData({ ...formData, image_url: '' });
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  {!imagePreview && <p className="text-sm text-gray-500">Henüz resim yüklenmedi</p>}
                  <label
                    htmlFor="image_upload"
                    className="flex items-center justify-center w-full h-24 sm:h-32 border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer hover:border-purple-500 transition-colors bg-gray-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Keep focus on current window
                      setTimeout(() => window.focus(), 100);
                    }}
                  >
                    <div className="text-center">
                      <Upload className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-xs sm:text-sm text-gray-600">{uploading ? 'Yükleniyor...' : 'Resim Yükle'}</p>
                    </div>
                  </label>
                  <input
                    id="image_upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="brand-image-input"
                    disabled={uploading}
                  />
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-purple-500 to-pink-500 w-full sm:w-auto" 
                  data-testid="save-brand-button"
                  disabled={uploading}
                >
                  {editingBrand ? 'Güncelle' : 'Ekle'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : brands.length === 0 ? (
        <Card className="text-center py-12 overflow-hidden">
          <CardContent>
            <Tag className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Hiç marka yok</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">Yeni bir marka ekleyerek başlayın</p>
          </CardContent>
        </Card>
      ) : filteredBrands.length === 0 ? (
        <Card className="text-center py-12 overflow-hidden">
          <CardContent>
            <Search className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Sonuç bulunamadı</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">"{searchQuery}" için eşleşen marka yok</p>
          </CardContent>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filteredBrands.map(b => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {filteredBrands.map(brand => (
                <SortableItem
                  key={brand.id}
                  brand={brand}
                  handleTogglePin={handleTogglePin}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  handleToggleArchive={handleToggleArchive}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
